import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Clock, BarChart3, TrendingUp, Star, ArrowRight, Star as StarIcon, X } from "lucide-react";
import ProgressJourney from "../../../components/ProgressJourney";
import DiscussWithMentor from "../../../components/mentors/DiscussWithMentor";
import { COLORS, HEADING_COLOR } from "../../../constants/colors";

const ExistingUserNoCareerHome = () => {
  const navigate = useNavigate();
  const { firstName } = useSelector((state) => state.auth);
  const { currentJob } = useSelector((state) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const careerPathOptions = [
    {
      title: "Explore Recommended Careers and Select a Target Career",
      description: "Discover AI-enhanced careers tailored to your skills.",
      button: { text: "Start", color: "purple", path: "/dashboard/career-discovery/career-recommendations" }
    },
    {
      title: "Keep my current Occupation",
      description: "Stay in your current role while building AI-resilience skills.",
      button: { text: "Keep my current occupation", color: "gradient", action: "openModal" }
    },
    {
      title: "Review Your AI Job Impact Results",
      description: "Take another look at how AI impacts your current role.",
      icon: <BarChart3 className="w-5 h-5 text-blue-600" />,
      button: { text: "Review Results", color: "blue", path: "/dashboard/task-automation-analysis" }
    }
  ];

  const getButtonClasses = (color) => {
    const baseClasses = "px-3 py-2 font-medium text-sm rounded-lg transition-colors flex items-center gap-2";
    const colorClasses = {
      purple: "bg-purple-600 text-white hover:bg-purple-700",
      gradient: "text-white hover:opacity-90",
      blue: "bg-blue-500 text-white hover:bg-blue-600"
    };
    return `${baseClasses} ${colorClasses[color]}`;
  };

  const getTagClasses = (color) => {
    const colorClasses = {
      purple: "bg-purple-100 text-purple-700",
      gray: "bg-gray-100 text-gray-600",
      blue: "bg-blue-100 text-blue-600"
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${colorClasses[color]}`;
  };

  const handleButtonClick = (option) => {
    if (option.button.action === "openModal") {
      setIsModalOpen(true);
    } else if (option.button.path) {
      navigate(option.button.path);
    }
  };

  const handleBackToDashboard = () => {
    setIsModalOpen(false);
    navigate(-1);
  };

  const handleTakeQuiz = () => {
    setIsModalOpen(false);
    navigate("/dashboard/quiz");
  };

  return (
    <div className=" min-h-screen py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold px-2" style={{ color: HEADING_COLOR }}>
            Hi {firstName || "Alex"}, let's find your ideal AI career path
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base md:text-lg px-2">
            Select your AI Proof Career or keep your current Career for targeted Upskilling
          </p>
        </div>

        {/* Your Journey Section */}
        <ProgressJourney currentStep={3} totalSteps={6} />

        {/* Main Content - Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Left Column - Choose a Career Path */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-400 rounded-xl p-4 sm:p-6 shadow-sm">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Choose a Career Path</h2>
              
              <div className="space-y-3 sm:space-y-4">
                {careerPathOptions.map((option, index) => (
                  <div key={index} className="p-3 sm:p-4 border border-gray-300 rounded-lg hover:border-purple-300 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {option.icon}
                          <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{option.title}</h3>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-3">{option.description}</p>
                      </div>
                      <button
                        onClick={() => handleButtonClick(option)}
                        className={`${getButtonClasses(option.button.color)} w-full sm:w-auto justify-center sm:justify-start`}
                      >
                        {option.button.text} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Discuss with Mentor */}
          <div className="lg:col-span-1">
            <DiscussWithMentor />
          </div>
        </div>
      </div>

      {/* Confirm Target Career Modal */}
      {isModalOpen && (
        <>
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 z-[9998]" onClick={() => setIsModalOpen(false)} />
          <div className="fixed inset-0 flex justify-center items-center z-[9999] p-4">
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md sm:max-w-lg md:w-[500px] max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Confirm Target Career</h2>
                  <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  </button>
                </div>
                <div className="mb-4">
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                    Your target career for upskilling is{" "}
                    <span className="font-bold text-gray-900">{currentJob || "Current Career"}</span>
                    . This will help us create a personalized learning pathway for you.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button onClick={handleBackToDashboard} className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium">
                    Back to dashboard
                  </button>
                  <button onClick={handleTakeQuiz} className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-white rounded-lg hover:opacity-90 transition-colors text-xs sm:text-sm font-medium" style={{ backgroundColor: COLORS.purple }}>
                    Take My AI Literacy Quiz now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExistingUserNoCareerHome;
